import styled, { css } from 'styled-components';
import { UiColors } from 'assets/styles/old/global';
import { TOP_BAR_HEIGHT, SEARCH_ROW_HEIGHT } from 'modules/Navigation/MobileChrome/styles';

// The mobile chrome (top bar + search row) is sticky and sits above this sheet in the
// stacking order, so anchor the sheet below it instead of covering the viewport — that
// keeps the sheet's own header visible and stops its content colliding with the search bar.
const HEADER_OFFSET = TOP_BAR_HEIGHT + SEARCH_ROW_HEIGHT;

export const Trigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: ${UiColors.fillWhiteColor};
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${UiColors.forestColor};
  cursor: pointer;
  text-align: left;

  svg.lead {
    width: 16px;
    height: 16px;
    color: ${UiColors.forestColor};
  }

  span.label {
    flex: 1;
  }

  svg.chevron {
    width: 16px;
    height: 16px;
    color: ${UiColors.dimGreyFillColor};
    transition: transform 0.15s ease;
  }

  ${({ open }) =>
    open &&
    css`
      svg.chevron {
        transform: rotate(180deg);
      }
    `}
`;

/* $railWidth: the desktop nav rail's on-screen width (useNavigationRail). This sheet is the page's
   below-1024px navigation, but the rail is already shown from 900px — so between the two the sheet
   starts right of the rail instead of under it, and below the desktop top bar (the rail comes with it),
   not the taller mobile chrome. */
export const Sheet = styled.div`
  position: fixed;
  top: ${({ $railWidth }) => ($railWidth ? TOP_BAR_HEIGHT : HEADER_OFFSET)}px;
  left: ${({ $railWidth }) => $railWidth || 0}px;
  right: 0;
  bottom: 0;
  background: ${UiColors.fillWhiteColor};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

export const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${UiColors.strokeColor};
  position: sticky;
  top: 0;
  background: ${UiColors.fillWhiteColor};
  z-index: 1;
`;

export const SheetTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${UiColors.forestColor};
`;

export const SheetClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${UiColors.forestColor};
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: ${UiColors.cloudColor};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const SheetGroup = styled.div`
  padding: 8px 0;

  & + & {
    border-top: 1px solid ${UiColors.strokeColor};
  }
`;

export const SheetGroupLabel = styled.div`
  padding: 4px 16px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${UiColors.brownColor};
`;

export const SheetItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 14px;
  color: ${UiColors.forestColor};
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
    color: ${UiColors.dimGreyFillColor};
  }

  &:hover {
    background: ${UiColors.cloudColor};
  }

  ${({ active }) =>
    active &&
    css`
      background: ${UiColors.peachColor};
      font-weight: 700;

      svg {
        color: ${UiColors.forestColor};
      }
    `}

  /* Indented child item (nested views under their parent section). */
  ${({ sub }) =>
    sub &&
    css`
      padding-left: 44px;
      font-size: 13px;

      svg {
        width: 15px;
        height: 15px;
      }
    `}
`;

export const SheetItemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  background: ${UiColors.headerTableGray};
  color: ${UiColors.dimGreyFillColor};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  margin-left: auto;

  ${({ tone }) =>
    tone === 'warn' &&
    css`
      background: ${UiColors.peachColor};
      color: ${UiColors.orangeColor};
    `}
`;
